// Live exclusive units (units endpoint) → the `apartments` rows the normal Availability tab renders. Pure.
import type { ExclusiveUnitRow, ExclusiveUnitStatus } from '~/types/exclusives';

// CMS-created catalogue projects (never in the portal Postgres) start here.
export const CATALOGUE_ONLY_PROJECT_ID_MIN = 900_000;

const SQFT_TO_SQM = 0.092903;

export const isCatalogueOnlyProject = (catalogueProjectId: number | null | undefined): boolean =>
  typeof catalogueProjectId === 'number' && catalogueProjectId >= CATALOGUE_ONLY_PROJECT_ID_MIN;

// Words the Availability tab matches on ("hold", "book", "sold").
const STATUS_WORD: Record<ExclusiveUnitStatus, string> = {
  available: 'available',
  on_hold: 'on hold',
  booked: 'booked',
  sold: 'sold',
};

export type ExclusiveApartment = {
  id: string;
  catalogueUnitId: number;
  number: string;
  status: string;
  exclusive: boolean;
  rooms_count: { en: string };
  title: { en: string };
  price: { min: string; currency: 'AED' };
  area_min?: { m2: string; ft2: string };
  floor: string | null;
  view: { en: string } | null;
};

// `markExclusive` = Postgres-backed project: its Available exclusive units get the gold ★ Exclusive row.
export const exclusiveRowsToApartments = (
  rows: readonly ExclusiveUnitRow[],
  options: { markExclusive?: boolean } = {},
): ExclusiveApartment[] =>
  rows.map((row) => {
    const label = row.bedrooms ?? row.unitType ?? '';
    return {
      id: String(row.catalogueUnitId),
      catalogueUnitId: row.catalogueUnitId,
      number: row.unitNumber,
      status: STATUS_WORD[row.status],
      exclusive: !!options.markExclusive && row.status === 'available',
      rooms_count: { en: label },
      title: { en: label },
      price: { min: String(row.priceAed ?? 0), currency: 'AED' },
      area_min:
        row.sizeSqft !== null && row.sizeSqft > 0
          ? { m2: String(row.sizeSqft * SQFT_TO_SQM), ft2: String(row.sizeSqft) }
          : undefined,
      floor: row.floor !== null ? String(row.floor) : null,
      view: row.view ? { en: row.view } : null,
    };
  });

// Apartments the Availability tab shows: catalogue-only projects list their exclusive units (every status, like the
// CRM); Postgres-backed projects put their Available exclusive units first, above the regular inventory.
export const mergeExclusiveApartments = <T>(
  catalogueProjectId: number,
  rows: readonly ExclusiveUnitRow[],
  existing: readonly T[],
): (T | ExclusiveApartment)[] =>
  isCatalogueOnlyProject(catalogueProjectId)
    ? exclusiveRowsToApartments(rows)
    : [
        ...exclusiveRowsToApartments(
          rows.filter((r) => r.status === 'available'),
          { markExclusive: true },
        ),
        ...existing,
      ];
